/**
 * Admin C2 preflight page — Aug 15, 2026
 * =======================================
 *
 * Reachable at:  /admin/preflight/c2  (admin-only via RoleRoute)
 *
 * Purpose: give the founder a normal in-app surface that invokes
 * the temporary LIVE C2 preflight endpoint (`GET /api/admin/preflight/c2`)
 * using the existing authenticated NDG session. No cookies to
 * copy, no dev tools, no curl.
 *
 * The page is read-only. It calls the endpoint once on mount and
 * on manual refresh. It never writes to R2, never touches media
 * quarantine, never alters config. Deletable together with the
 * backend `routes/preflight_c2.py` when the C2 preflight window
 * closes.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, ShieldCheck, ShieldAlert, Clipboard, Loader2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import GC64LiveVerification from "./GC64LiveVerification";

const API = process.env.REACT_APP_BACKEND_URL;

// PASS criteria — mirrors the founder-authored C2 preflight brief.
function classify(data) {
  if (!data) return {};
  const fa = data.founder_admin || {};
  return {
    A1_derivative_worker: (
      data.derivative_worker_enabled === true &&
      data.derivative_worker_supervisor_state === "RUNNING"
    ),
    A2_r2_probe: data.r2_probe === "ok",
    A3_stripe_mode: data.stripe_mode_effective === "test",
    A4_founder_admin: (
      fa.email === "johncook79@icloud.com" &&
      fa.role === "admin" &&
      fa.founder_free_uploads === true
    ),
  };
}

function Row({ label, ok, value, criterion, children }) {
  const cls = ok
    ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800"
    : "bg-rose-50 border-rose-200 dark:bg-rose-900/20 dark:border-rose-800";
  const Icon = ok ? ShieldCheck : ShieldAlert;
  const iconCls = ok ? "text-emerald-600" : "text-rose-600";
  return (
    <div
      data-testid={`c2-check-${label.split(" ")[0].toLowerCase()}`}
      className={`p-4 rounded-xl border-2 ${cls} flex items-start gap-3`}
    >
      <Icon className={`w-6 h-6 flex-shrink-0 ${iconCls}`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="font-bold text-slate-900 dark:text-slate-100">
          {label}: {ok ? "PASS" : "FAIL"}
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
          <span className="font-semibold">Criterion:</span> {criterion}
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 break-words">
          <span className="font-semibold">Observed:</span> {value}
        </p>
        {children}
      </div>
    </div>
  );
}

// Aug 15 2026 · Stage 2 · Founder free-uploads inline control.
// Only renders for the authoritative founder viewing their OWN
// preflight (mechanically gated below). Delegate admins never see
// this button. Reuses the existing admin-only endpoint
// POST /api/upload-pricing/founder-free-mode {enabled: bool}.
const AUTHORITATIVE_FOUNDER_EMAIL = "johncook79@icloud.com";

function FounderFreeUploadsControl({ founderAdmin, sessionEmail, onChanged }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = founderAdmin?.founder_free_uploads;    // true | false | null
  const source = founderAdmin?.founder_free_uploads_source; // "user.founder_free_uploads" | "unset" | "unknown"

  // Visibility gate: preflight resolved a founder AND the
  // calling admin's own email matches the authoritative founder.
  const isFounderResolved = founderAdmin?.email === AUTHORITATIVE_FOUNDER_EMAIL;
  const callerIsFounder = sessionEmail && sessionEmail.toLowerCase()
                          === AUTHORITATIVE_FOUNDER_EMAIL;
  if (!isFounderResolved || !callerIsFounder) return null;

  const next = !current;
  const verb = next ? "Enable" : "Disable";

  const apply = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/upload-pricing/founder-free-mode`,
        { enabled: next },
        { withCredentials: true },
      );
      toast.success(`Founder free uploads ${next ? "enabled" : "disabled"}`);
      setConfirming(false);
      // Re-run the preflight so the A.4 row reflects the new state.
      if (onChanged) setTimeout(onChanged, 400);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Request failed";
      toast.error(`Toggle failed: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <div className="mt-3">
        <button
          data-testid="c2-ffu-toggle"
          onClick={() => setConfirming(true)}
          className="text-sm font-bold px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white min-h-[36px]"
        >
          {verb} founder free uploads for {AUTHORITATIVE_FOUNDER_EMAIL}
        </button>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
          Affects this admin account only · Stripe mode is unchanged · other
          users are unaffected · fully reversible via the same button.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="c2-ffu-confirm"
      className="mt-3 p-3 rounded-lg border-2 border-amber-400 bg-white dark:bg-slate-900"
    >
      <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
        Confirm state change
      </p>
      <p className="text-xs text-slate-700 dark:text-slate-200 mt-2">
        Currently: <code className="font-mono">founder_free_uploads = {JSON.stringify(current)}</code>
        {source && <> · source: <code className="font-mono">{source}</code></>}
      </p>
      <p className="text-xs text-slate-700 dark:text-slate-200 mt-1">
        After: <code className="font-mono">founder_free_uploads = {JSON.stringify(next)}</code>
      </p>
      <ul className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 list-disc pl-5 space-y-0.5">
        <li>Only your own admin record ({AUTHORITATIVE_FOUNDER_EMAIL}) is changed.</li>
        <li>Stripe operating mode is NOT changed by this action.</li>
        <li>No other user&apos;s billing or upload state is changed.</li>
        <li>Media quarantine is unaffected.</li>
      </ul>
      <div className="mt-3 flex gap-2">
        <button
          data-testid="c2-ffu-cancel"
          onClick={() => setConfirming(false)}
          disabled={busy}
          className="flex-1 px-3 py-2 rounded-lg border-2 border-slate-300 dark:border-slate-700 font-bold text-sm min-h-[36px]"
        >
          Cancel
        </button>
        <button
          data-testid="c2-ffu-confirm-apply"
          onClick={apply}
          disabled={busy}
          className="flex-1 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-bold text-sm min-h-[36px]"
        >
          {busy ? "Applying…" : `Yes, ${verb.toLowerCase()}`}
        </button>
      </div>
    </div>
  );
}

export default function PreflightC2Page() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/api/admin/preflight/c2`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Request failed";
      setErr(String(detail));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success("Preflight JSON copied");
    } catch (_e) {
      toast.error("Could not copy — long-press the JSON below to select");
    }
  };

  const results = classify(data);
  const allPass = data && Object.values(results).every(Boolean);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-safe">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-4 flex items-center gap-2">
          <Link
            to="/admin"
            data-testid="c2-back-to-admin"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
        </div>

        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300">
            Media C2 · Preflight
          </p>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
            LIVE readiness check
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">
            Runs the four preflight probes against this deployment&apos;s own
            runtime. Nothing is written and no configuration is changed.
            Media quarantine remains ON.
          </p>
        </div>

        {/* Overall verdict */}
        {data && !err && (
          <div
            data-testid="c2-overall-verdict"
            className={`mb-6 p-4 rounded-2xl border-2 ${
              allPass
                ? "bg-emerald-50 border-emerald-300 dark:bg-emerald-900/20 dark:border-emerald-700"
                : "bg-amber-50 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700"
            }`}
          >
            <p className="text-base font-bold text-slate-900 dark:text-slate-100">
              {allPass
                ? "PASS · READY_FOR_QUARANTINE_LIFT_AUTHORISATION"
                : "BLOCKED — one or more preflight checks failed. See below."}
            </p>
          </div>
        )}

        {/* Error banner */}
        {err && (
          <div
            data-testid="c2-error"
            className="mb-6 p-4 rounded-2xl border-2 border-rose-300 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-700"
          >
            <p className="font-bold text-rose-900 dark:text-rose-100">
              Preflight request failed
            </p>
            <p className="text-sm text-rose-800 dark:text-rose-200 mt-1 break-words">
              {err}
            </p>
          </div>
        )}

        {/* Individual checks */}
        {data && (
          <div className="space-y-3 mb-6">
            <Row
              label="A.1 Derivative worker"
              ok={results.A1_derivative_worker}
              criterion={'derivative_worker_enabled == true AND derivative_worker_supervisor_state == "RUNNING"'}
              value={`enabled=${JSON.stringify(data.derivative_worker_enabled)} · supervisor=${data.derivative_worker_supervisor_state}`}
            />
            <Row
              label="A.2 R2 write/read/delete"
              ok={results.A2_r2_probe}
              criterion={'r2_probe == "ok"'}
              value={String(data.r2_probe)}
            />
            <Row
              label="A.3 Stripe rehearsal safety"
              ok={results.A3_stripe_mode}
              criterion={'stripe_mode_effective == "test"'}
              value={String(data.stripe_mode_effective)}
            />
            <Row
              label="A.4 Founder free uploads"
              ok={results.A4_founder_admin}
              criterion={'founder_admin.email == johncook79@icloud.com AND role == admin AND founder_free_uploads == true'}
              value={`email=${data.founder_admin?.email ?? "null"} · role=${data.founder_admin?.role ?? "null"} · founder_free_uploads=${JSON.stringify(data.founder_admin?.founder_free_uploads)} · source=${data.founder_admin?.founder_free_uploads_source ?? "unknown"}`}
            >
              <FounderFreeUploadsControl
                founderAdmin={data.founder_admin}
                sessionEmail={user?.email}
                onChanged={load}
              />
            </Row>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 mb-6">
          <button
            data-testid="c2-refresh"
            onClick={load}
            disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-bold min-h-[44px]"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Re-run preflight
          </button>
          {data && (
            <button
              data-testid="c2-copy-json"
              onClick={copyJson}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 font-bold min-h-[44px]"
            >
              <Clipboard className="w-4 h-4" /> Copy raw JSON
            </button>
          )}
        </div>

        {/* Raw JSON for evidence/paste */}
        {data && (
          <details className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <summary
              data-testid="c2-raw-toggle"
              className="cursor-pointer px-4 py-3 font-semibold text-sm text-slate-700 dark:text-slate-200"
            >
              Raw preflight response
            </summary>
            <pre
              data-testid="c2-raw-json"
              className="px-4 pb-4 text-xs overflow-x-auto whitespace-pre-wrap break-words text-slate-800 dark:text-slate-100 select-all"
            >
              {JSON.stringify(data, null, 2)}
            </pre>
          </details>
        )}

        {/* GC64 · Intake→Media-Job Bridge verification (Feb 13, 2026) */}
        <GC64LiveVerification />
      </div>
    </div>
  );
}
